import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaTrophy, FaUsers, FaClock, FaCrown, FaMoneyBillWave, FaLock } from 'react-icons/fa';
import toast from 'react-hot-toast';
import Navbar from '../components/Layout/Navbar';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';

const STATUS_MAP = {
  registration: { label: 'Inscriptions ouvertes', color: 'text-blue-400',   dot: 'bg-blue-400'   },
  group_stage:  { label: 'Phase de groupes',       color: 'text-yellow-400', dot: 'bg-yellow-400' },
  knockout:     { label: 'Élimination directe',    color: 'text-orange-400', dot: 'bg-orange-400' },
  finished:     { label: 'Terminé',                color: 'text-gray-400',   dot: 'bg-gray-400'   },
};

const FORMAT_LABEL = {
  mini:     '4 joueurs',
  standard: '16 joueurs',
  grand:    '32 joueurs',
};

function ChampionshipCard({ c, onRegister, registering }) {
  const navigate  = useNavigate();
  const { user }  = useAuth();
  const s = STATUS_MAP[c.status] || STATUS_MAP.registration;
  const isLocked  = c.premiumOnly && user?.plan !== 'premium';
  const isFull    = c.participantCount >= c.maxPlayers;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="card cursor-pointer hover:border-qfl-gold/30 transition-colors"
      style={{ border: '1px solid rgba(255,255,255,0.07)' }}
      onClick={() => navigate(`/championships/${c.id}`)}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <h3 className="text-white font-black text-base truncate"
              style={{ fontFamily: "'Rajdhani', sans-serif" }}>{c.nom}</h3>
            {c.premiumOnly && <FaCrown size={12} className="text-qfl-gold flex-shrink-0" />}
          </div>
          <div className="flex items-center gap-1.5">
            <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
            <span className={`text-xs font-semibold ${s.color}`}
              style={{ fontFamily: "'Rajdhani', sans-serif" }}>{s.label}</span>
          </div>
        </div>
        <div className="text-3xl ml-2">
          {c.format === 'mini' ? '🏅' : c.format === 'grand' ? '🏆' : '⚽'}
        </div>
      </div>

      {/* Prizes */}
      {(c.prizeMoney || c.prizeCoins > 0) && (
        <div className="flex items-center gap-3 mb-3 p-2.5 rounded-xl"
          style={{ background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.15)' }}>
          {c.prizeMoney && (
            <div className="flex items-center gap-1.5">
              <FaMoneyBillWave size={13} className="text-green-400" />
              <span className="text-green-400 font-black text-sm"
                style={{ fontFamily: "'Rajdhani', sans-serif" }}>{c.prizeMoney}</span>
            </div>
          )}
          {c.prizeMoney && c.prizeCoins > 0 && <span className="text-white/20 text-xs">+</span>}
          {c.prizeCoins > 0 && (
            <span className="text-qfl-gold text-xs font-bold">{c.prizeCoins} pièces</span>
          )}
        </div>
      )}

      {c.prizeDescription && (
        <p className="text-white/40 text-xs mb-3 italic">{c.prizeDescription}</p>
      )}

      {/* Infos */}
      <div className="flex items-center gap-4 text-white/40 text-xs mb-4">
        <span className="flex items-center gap-1">
          <FaUsers size={10} /> {c.participantCount}/{c.maxPlayers}
        </span>
        <span className="text-white/20">{FORMAT_LABEL[c.format]}</span>
        {c.registrationDeadline && c.status === 'registration' && (
          <span className="flex items-center gap-1">
            <FaClock size={10} />
            fin {new Date(c.registrationDeadline).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}
          </span>
        )}
      </div>

      {/* Bouton inscription */}
      {c.status === 'registration' && (
        <button
          onClick={(e) => { e.stopPropagation(); onRegister(c); }}
          disabled={isLocked || isFull || registering === c.id}
          className="w-full py-2.5 rounded-xl font-black text-sm uppercase tracking-widest transition-opacity disabled:opacity-40"
          style={{
            fontFamily: "'Rajdhani', sans-serif",
            background: isLocked
              ? 'rgba(255,255,255,0.08)'
              : 'linear-gradient(135deg, #f0b429, #c97d08)',
            color: isLocked ? 'rgba(255,255,255,0.4)' : '#0d2b1a',
          }}
        >
          {registering === c.id ? (
            <span className="flex items-center justify-center gap-2">
              <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
              INSCRIPTION...
            </span>
          ) : isLocked ? (
            <span className="flex items-center justify-center gap-2"><FaLock size={12} /> PREMIUM REQUIS</span>
          ) : isFull ? 'COMPLET' : (
            c.entryFee > 0 ? `S'INSCRIRE — ${c.entryFee} FCFA` : "S'INSCRIRE GRATUITEMENT"
          )}
        </button>
      )}

      {c.status !== 'registration' && (
        <button
          onClick={(e) => e.stopPropagation()}
          className="w-full py-2.5 rounded-xl font-black text-sm uppercase tracking-widest text-white/60 bg-white/5 border border-white/10"
          style={{ fontFamily: "'Rajdhani', sans-serif" }}
          onClick={() => navigate(`/championships/${c.id}`)}
        >
          VOIR LE TABLEAU
        </button>
      )}
    </motion.div>
  );
}

function Championships() {
  const { user } = useAuth();
  const [championships, setChampionships] = useState([]);
  const [loading, setLoading]             = useState(true);
  const [registering, setRegistering]     = useState(null);

  useEffect(() => {
    api.get('/championships')
      .then(({ data }) => setChampionships(data.championships || []))
      .catch(() => setChampionships([]))
      .finally(() => setLoading(false));
  }, []);

  const handleRegister = async (c) => {
    if (!user) { toast.error('Connectez-vous pour vous inscrire'); return; }
    setRegistering(c.id);
    try {
      const { data } = await api.post(`/championships/${c.id}/register`);
      toast.success(data.message);
      setChampionships((prev) => prev.map((ch) =>
        ch.id === c.id ? { ...ch, participantCount: ch.participantCount + 1 } : ch
      ));
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Erreur');
    } finally {
      setRegistering(null);
    }
  };

  const active   = championships.filter((c) => c.status !== 'finished' && c.status !== 'cancelled');
  const finished = championships.filter((c) => c.status === 'finished');

  return (
    <div className="app-container flex flex-col min-h-screen" style={{ paddingBottom: 80 }}>
      <Navbar title="Championnats" showBack />

      <div className="px-4 pt-4">
        {/* Header */}
        <div className="mb-5">
          <h1 className="text-2xl font-black text-qfl-gold uppercase tracking-widest"
            style={{ fontFamily: "'Rajdhani', sans-serif" }}>
            🏆 CHAMPIONNATS
          </h1>
          <p className="text-white/40 text-sm mt-1">
            Tournois avec poules + élimination directe — prizes en argent réel
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-10 h-10 rounded-full border-4 border-qfl-gold border-t-transparent animate-spin" />
          </div>
        ) : (
          <>
            {active.length === 0 && finished.length === 0 ? (
              <div className="text-center py-20">
                <div className="text-6xl mb-4">🏆</div>
                <p className="text-white/40 text-base font-bold"
                  style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                  Aucun championnat en cours
                </p>
                <p className="text-white/25 text-sm mt-1">Revenez bientôt !</p>
              </div>
            ) : (
              <div className="flex flex-col gap-4">
                {active.map((c) => (
                  <ChampionshipCard
                    key={c.id} c={c}
                    onRegister={handleRegister}
                    registering={registering}
                  />
                ))}

                {finished.length > 0 && (
                  <>
                    <p className="text-white/30 text-xs uppercase tracking-widest mt-2 mb-1"
                      style={{ fontFamily: "'Rajdhani', sans-serif" }}>TERMINÉS</p>
                    {finished.map((c) => (
                      <ChampionshipCard
                        key={c.id} c={c}
                        onRegister={handleRegister}
                        registering={registering}
                      />
                    ))}
                  </>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default Championships;
